import { useState, useRef } from 'react';
import { uploadToCloudinary, submitApplication }  from '../lib/firestoreService';

/* ─── Step definitions ──────────────────────────────────── */
const STEPS = [
  { id: 1, label: 'Personal',    icon: '👤', title: 'Personal Information' },
  { id: 2, label: 'Position',    icon: '💼', title: 'Position Information' },
  { id: 3, label: 'Experience',  icon: '🏢', title: 'Work Experience' },
  { id: 4, label: 'Education',   icon: '🎓', title: 'Education' },
  { id: 5, label: 'Skills',      icon: '⚡', title: 'Skills & Software' },
  { id: 6, label: 'Documents',   icon: '📄', title: 'Resume & Cover Letter' },
  { id: 7, label: 'References',  icon: '🤝', title: 'References' },
  { id: 8, label: 'Declaration', icon: '✍️',  title: 'Declaration & Submit' },
];

const EMPTY = {
  // §1
  fullName: '', email: '', phone: '', address: '', cityStateCountry: '', authorizedToWork: '',
  // §2
  position: '', positionOther: '', startDate: '', employmentType: '', salary: '',
  // §3
  employer: '', jobTitle: '', employmentPeriod: '', responsibilities: '', reasonLeaving: '',
  // §4
  qualification: '', school: '', fieldOfStudy: '', graduationYear: '',
  // §5
  relevantSkills: '', softwareExperience: '',
  // §6 — stored as objects { url, name }
  resumeFile: null, coverLetterFile: null,
  // §7
  refName: '', refRelationship: '', refContact: '',
  // §8
  declarationConfirmed: false, signature: '',
};

/* ─── Reusable field components ─────────────────────────── */
function Label({ children, required }) {
  return (
    <label className="block text-sm font-semibold mb-1.5" style={{ color: '#cbd5e1' }}>
      {children}{required && <span className="text-rose-400 ml-1">*</span>}
    </label>
  );
}

function Input({ error, ...props }) {
  return (
    <input
      {...props}
      className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-200"
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${error ? '#f43f5e' : 'rgba(255,255,255,0.1)'}`,
        color: 'white',
        outline: 'none',
        fontFamily: 'inherit',
      }}
      onFocus={e => e.target.style.borderColor = '#e2c27d'}
      onBlur={e => e.target.style.borderColor = error ? '#f43f5e' : 'rgba(255,255,255,0.1)'}
    />
  );
}

function Textarea({ error, ...props }) {
  return (
    <textarea
      {...props}
      rows={4}
      className="w-full px-4 py-3 rounded-xl text-sm resize-none transition-all duration-200"
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${error ? '#f43f5e' : 'rgba(255,255,255,0.1)'}`,
        color: 'white',
        outline: 'none',
        fontFamily: 'inherit',
      }}
      onFocus={e => e.target.style.borderColor = '#e2c27d'}
      onBlur={e => e.target.style.borderColor = error ? '#f43f5e' : 'rgba(255,255,255,0.1)'}
    />
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(opt => (
        <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
          <div
            className="w-5 h-5 rounded-full flex items-center justify-center transition-all"
            style={{
              border: `2px solid ${value === opt.value ? '#e2c27d' : 'rgba(255,255,255,0.2)'}`,
              background: value === opt.value ? '#e2c27d22' : 'transparent',
            }}
          >
            {value === opt.value && <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#e2c27d' }} />}
          </div>
          <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={onChange} className="sr-only" />
          <span className="text-sm" style={{ color: value === opt.value ? '#e2c27d' : '#94a3b8' }}>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ name, options, value, onChange }) {
  const vals = value ? value.split(',').map(v => v.trim()).filter(Boolean) : [];
  const toggle = (v) => {
    const next = vals.includes(v) ? vals.filter(x => x !== v) : [...vals, v];
    onChange({ target: { name, value: next.join(', ') } });
  };
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(opt => {
        const checked = vals.includes(opt.value);
        return (
          <button key={opt.value} type="button" onClick={() => toggle(opt.value)}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              background: checked ? 'rgba(226,194,125,0.12)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${checked ? '#e2c27d' : 'rgba(255,255,255,0.1)'}`,
              color: checked ? '#e2c27d' : '#94a3b8',
            }}
          >
            <div className="w-4 h-4 rounded flex items-center justify-center"
              style={{ background: checked ? '#e2c27d' : 'transparent', border: checked ? 'none' : '1.5px solid rgba(255,255,255,0.2)' }}>
              {checked && <span className="text-xs text-[#1a1200] font-black">✓</span>}
            </div>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="mt-1.5 text-xs text-rose-400 flex items-center gap-1">⚠ {msg}</p>;
}

/* ─── File Upload Component ─────────────────────────────── */
function FileUpload({ label, accept, value, onChange, uploading, required }) {
  const ref = useRef();
  return (
    <div>
      <Label required={required}>{label}</Label>
      <div
        onClick={() => ref.current.click()}
        className="relative flex flex-col items-center justify-center gap-3 p-8 rounded-2xl cursor-pointer transition-all duration-200 group"
        style={{
          border: `2px dashed ${value ? '#e2c27d' : 'rgba(255,255,255,0.12)'}`,
          background: value ? 'rgba(226,194,125,0.05)' : 'rgba(255,255,255,0.02)',
        }}
        onMouseEnter={e => { if (!value) e.currentTarget.style.borderColor = 'rgba(226,194,125,0.4)'; }}
        onMouseLeave={e => { if (!value) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
      >
        <input ref={ref} type="file" accept={accept} className="sr-only" onChange={onChange} />
        {uploading ? (
          <>
            <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: '#e2c27d', borderTopColor: 'transparent' }} />
            <p className="text-sm" style={{ color: '#94a3b8' }}>Uploading to Cloudinary…</p>
          </>
        ) : value ? (
          <>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: 'rgba(226,194,125,0.15)' }}>📎</div>
            <div className="text-center">
              <p className="text-sm font-semibold" style={{ color: '#e2c27d' }}>{value.name}</p>
              <p className="text-xs mt-1" style={{ color: '#64748b' }}>Click to replace</p>
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: 'rgba(255,255,255,0.06)' }}>☁️</div>
            <div className="text-center">
              <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>Drop file here or <span style={{ color: '#e2c27d' }}>click to browse</span></p>
              <p className="text-xs mt-1" style={{ color: '#475569' }}>PDF, DOC, DOCX — max 10MB</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Validation ─────────────────────────────────────────── */
function validateStep(step, form) {
  const e = {};
  if (step === 1) {
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Required';
    if (!form.cityStateCountry.trim()) e.cityStateCountry = 'Required';
    if (!form.authorizedToWork) e.authorizedToWork = 'Required';
  }
  if (step === 2) {
    if (!form.position) e.position = 'Select a position';
    if (form.position === 'Other' && !form.positionOther.trim()) e.positionOther = 'Please specify';
    if (!form.startDate) e.startDate = 'Required';
    if (!form.employmentType) e.employmentType = 'Required';
  }
  if (step === 3) {
    if (!form.employer.trim()) e.employer = 'Required';
    if (!form.jobTitle.trim()) e.jobTitle = 'Required';
    if (!form.responsibilities.trim()) e.responsibilities = 'Required';
  }
  if (step === 4) {
    if (!form.qualification.trim()) e.qualification = 'Required';
    if (!form.school.trim()) e.school = 'Required';
  }
  if (step === 5) {
    if (!form.relevantSkills.trim()) e.relevantSkills = 'Required';
  }
  if (step === 6) {
    if (!form.resumeFile) e.resumeFile = 'Resume is required';
  }
  if (step === 7) {
    if (!form.refName.trim()) e.refName = 'Required';
    if (!form.refContact.trim()) e.refContact = 'Required';
  }
  if (step === 8) {
    if (!form.declarationConfirmed) e.declarationConfirmed = 'You must confirm this declaration';
    if (!form.signature.trim()) e.signature = 'Signature required';
  }
  return e;
}

/* ─── Main Component ─────────────────────────────────────── */
export default function JobApplicationForm() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState({ resume: false, cover: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const set = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => { const n = { ...p }; delete n[name]; return n; });
  };
  const handle = e => set(e.target.name, e.target.value);

  const handleFile = async (field, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const key = field === 'resumeFile' ? 'resume' : 'cover';
    setUploading(p => ({ ...p, [key]: true }));
    try {
      const result = await uploadToCloudinary(file);
      set(field, { url: result.url, name: file.name, publicId: result.publicId });
    } catch {
      setErrors(p => ({ ...p, [field]: 'Upload failed — please try again' }));
    } finally {
      setUploading(p => ({ ...p, [key]: false }));
    }
  };

  const next = () => {
    const e = validateStep(step, form);
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setStep(s => Math.min(s + 1, 8));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => { setErrors({}); setStep(s => Math.max(s - 1, 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSubmit = async () => {
    const e = validateStep(8, form);
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      await submitApplication({
        personal: { fullName: form.fullName, email: form.email, phone: form.phone, address: form.address, cityStateCountry: form.cityStateCountry, authorizedToWork: form.authorizedToWork },
        position: { position: form.position, positionOther: form.positionOther, startDate: form.startDate, employmentType: form.employmentType, salary: form.salary },
        experience: { employer: form.employer, jobTitle: form.jobTitle, employmentPeriod: form.employmentPeriod, responsibilities: form.responsibilities, reasonLeaving: form.reasonLeaving },
        education: { qualification: form.qualification, school: form.school, fieldOfStudy: form.fieldOfStudy, graduationYear: form.graduationYear },
        skills: { relevantSkills: form.relevantSkills, softwareExperience: form.softwareExperience },
        documents: { resume: form.resumeFile, coverLetter: form.coverLetterFile },
        references: { name: form.refName, relationship: form.refRelationship, contact: form.refContact },
        declaration: { confirmed: form.declarationConfirmed, signature: form.signature },
      });
      setSubmitted(true);
    } catch {
      setSubmitError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  /* ── SUCCESS SCREEN ── */
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0d1117', fontFamily: "'Lora', serif" }}>
        <div className="max-w-lg w-full text-center">
          <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl mx-auto mb-8"
            style={{ background: 'linear-gradient(135deg, #e2c27d22, #e2c27d44)', border: '2px solid #e2c27d44' }}>
            🎉
          </div>
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#e2c27d', fontFamily: "'Playfair Display', serif" }}>Application Submitted!</h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: '#94a3b8' }}>
            Thank you, <strong style={{ color: 'white' }}>{form.fullName}</strong>. Your application for{' '}
            <strong style={{ color: 'white' }}>{form.position === 'Other' ? form.positionOther : form.position}</strong> has been received.
            We'll be in touch at <strong style={{ color: '#e2c27d' }}>{form.email}</strong>.
          </p>
          <div className="p-5 rounded-2xl mb-8 text-left space-y-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#64748b' }}>Summary</p>
            {[['Position', form.position === 'Other' ? form.positionOther : form.position], ['Employment Type', form.employmentType], ['Start Date', form.startDate], ['Submitted By', form.email]].map(([k, v]) => v && (
              <div key={k} className="flex justify-between text-sm">
                <span style={{ color: '#64748b' }}>{k}</span>
                <span style={{ color: '#e2e8f0' }}>{v}</span>
              </div>
            ))}
          </div>
          <button onClick={() => { setSubmitted(false); setForm(EMPTY); setStep(1); }}
            className="px-8 py-3 rounded-xl font-bold text-sm transition-all hover:scale-105"
            style={{ background: '#e2c27d', color: '#0d1117' }}>
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0d1117', fontFamily: "'Lora', Georgia, serif", color: 'white' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Lora:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        ::placeholder { color: rgba(148,163,184,0.4) !important; }
        select option { background: #1e293b; color: white; }
        .step-enter { animation: stepIn 0.4s cubic-bezier(0.16,1,0.3,1) forwards; }
        @keyframes stepIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
      `}</style>

      {/* ── HEADER ── */}
      <header className="border-b sticky top-0 z-50" style={{ background: 'rgba(13,17,23,0.97)', backdropFilter: 'blur(20px)', borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm" style={{ background: '#e2c27d', color: '#0d1117' }}>AX</div>
            <div>
              <p className="text-xs font-bold" style={{ color: '#e2c27d', fontFamily: "'Lora', serif", letterSpacing: '0.08em' }}>ATLASROUTELOGISTICS</p>
              <p className="text-xs" style={{ color: '#475569' }}>Careers Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold" style={{ color: '#94a3b8' }}>Step {step} of {STEPS.length}</p>
              <p className="text-xs" style={{ color: '#475569' }}>{STEPS[step-1].title}</p>
            </div>
            <div className="w-12 h-12 rounded-full flex items-center justify-center relative" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                <circle cx="24" cy="24" r="20" fill="none" stroke="#e2c27d" strokeWidth="3"
                  strokeDasharray={`${progress * 1.257} 125.7`} strokeLinecap="round" />
              </svg>
              <span className="text-xs font-bold" style={{ color: '#e2c27d' }}>{step}</span>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-0.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
          <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #e2c27d, #f5d78e)' }} />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">

        {/* ── Step nav pills (desktop) ── */}
        <div className="hidden lg:flex items-center gap-1 mb-10 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <div key={s.id} className="flex items-center gap-1">
                <button
                  onClick={() => done && setStep(s.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all"
                  style={{
                    background: active ? 'rgba(226,194,125,0.12)' : done ? 'rgba(255,255,255,0.04)' : 'transparent',
                    border: `1px solid ${active ? '#e2c27d44' : 'transparent'}`,
                    color: active ? '#e2c27d' : done ? '#94a3b8' : '#475569',
                    cursor: done ? 'pointer' : 'default',
                  }}
                >
                  <span>{done ? '✓' : s.icon}</span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <span style={{ color: '#2d3748', fontSize: '10px' }}>›</span>}
              </div>
            );
          })}
        </div>

        {/* ── Step heading ── */}
        <div className="mb-8 step-enter" key={`heading-${step}`}>
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0"
              style={{ background: 'rgba(226,194,125,0.1)', border: '1px solid rgba(226,194,125,0.2)' }}>
              {STEPS[step-1].icon}
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#64748b' }}>Section {step}</p>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: '#f8fafc' }}>
                {STEPS[step-1].title}
              </h2>
            </div>
          </div>
        </div>

        {/* ── FORM CARD ── */}
        <div className="rounded-3xl p-6 md:p-10 step-enter" key={`card-${step}`} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>

          {/* STEP 1 — Personal */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Full Name</Label>
                  <Input name="fullName" value={form.fullName} onChange={handle} placeholder="John Adebayo" error={errors.fullName} />
                  <FieldError msg={errors.fullName} />
                </div>
                <div>
                  <Label required>Email Address</Label>
                  <Input name="email" type="email" value={form.email} onChange={handle} placeholder="john@example.com" error={errors.email} />
                  <FieldError msg={errors.email} />
                </div>
                <div>
                  <Label required>Phone Number</Label>
                  <Input name="phone" value={form.phone} onChange={handle} placeholder="+1 555 000 0000" error={errors.phone} />
                  <FieldError msg={errors.phone} />
                </div>
                <div>
                  <Label>Home Address</Label>
                  <Input name="address" value={form.address} onChange={handle} placeholder="123 Main St" />
                </div>
                <div className="md:col-span-2">
                  <Label required>City / State / Country</Label>
                  <Input name="cityStateCountry" value={form.cityStateCountry} onChange={handle} placeholder="Houston, Texas, USA" error={errors.cityStateCountry} />
                  <FieldError msg={errors.cityStateCountry} />
                </div>
              </div>
              <div>
                <Label required>Are you legally authorized to work in the United States?</Label>
                <div className="mt-2">
                  <RadioGroup name="authorizedToWork" value={form.authorizedToWork} onChange={handle}
                    options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]} />
                </div>
                <FieldError msg={errors.authorizedToWork} />
              </div>
            </div>
          )}

          {/* STEP 2 — Position */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <Label required>Position Applying For</Label>
                <div className="mt-2">
                  <CheckboxGroup name="position"
                    value={form.position}
                    onChange={e => set('position', e.target.value)}
                    options={[
                      { value: 'Procurement Officer', label: '📦 Procurement Officer' },
                      { value: 'Sales Representative', label: '📊 Sales Representative' },
                      { value: 'Logistics Coordinator', label: '🚚 Logistics Coordinator' },
                      { value: 'Other', label: '➕ Other' },
                    ]}
                  />
                </div>
                <FieldError msg={errors.position} />
              </div>
              {form.position?.includes('Other') && (
                <div>
                  <Label required>Please specify position</Label>
                  <Input name="positionOther" value={form.positionOther} onChange={handle} placeholder="Enter position title" error={errors.positionOther} />
                  <FieldError msg={errors.positionOther} />
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Available Start Date</Label>
                  <Input name="startDate" type="date" value={form.startDate} onChange={handle} error={errors.startDate} />
                  <FieldError msg={errors.startDate} />
                </div>
                <div>
                  <Label required>Employment Type</Label>
                  <div className="mt-2">
                    <RadioGroup name="employmentType" value={form.employmentType} onChange={handle}
                      options={[{ value: 'Full-Time', label: 'Full-Time' }, { value: 'Part-Time', label: 'Part-Time' }, { value: 'Remote', label: 'Remote' }]} />
                  </div>
                  <FieldError msg={errors.employmentType} />
                </div>
                <div className="md:col-span-2">
                  <Label>Expected Salary</Label>
                  <Input name="salary" value={form.salary} onChange={handle} placeholder="$60,000 / year" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Experience */}
          {step === 3 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Current / Most Recent Employer</Label>
                  <Input name="employer" value={form.employer} onChange={handle} placeholder="Company Name" error={errors.employer} />
                  <FieldError msg={errors.employer} />
                </div>
                <div>
                  <Label required>Job Title</Label>
                  <Input name="jobTitle" value={form.jobTitle} onChange={handle} placeholder="Senior Analyst" error={errors.jobTitle} />
                  <FieldError msg={errors.jobTitle} />
                </div>
                <div className="md:col-span-2">
                  <Label>Employment Period</Label>
                  <Input name="employmentPeriod" value={form.employmentPeriod} onChange={handle} placeholder="Jan 2022 – Present" />
                </div>
              </div>
              <div>
                <Label required>Key Responsibilities</Label>
                <Textarea name="responsibilities" value={form.responsibilities} onChange={handle}
                  placeholder="Describe your main duties and achievements…" error={errors.responsibilities} />
                <FieldError msg={errors.responsibilities} />
              </div>
              <div>
                <Label>Reason for Leaving</Label>
                <Input name="reasonLeaving" value={form.reasonLeaving} onChange={handle} placeholder="Career growth opportunity" />
              </div>
            </div>
          )}

          {/* STEP 4 — Education */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Highest Qualification</Label>
                  <Input name="qualification" value={form.qualification} onChange={handle} placeholder="Bachelor's Degree" error={errors.qualification} />
                  <FieldError msg={errors.qualification} />
                </div>
                <div>
                  <Label required>School / University</Label>
                  <Input name="school" value={form.school} onChange={handle} placeholder="University of Cali" error={errors.school} />
                  <FieldError msg={errors.school} />
                </div>
                <div>
                  <Label>Field of Study</Label>
                  <Input name="fieldOfStudy" value={form.fieldOfStudy} onChange={handle} placeholder="Supply Chain Management" />
                </div>
                <div>
                  <Label>Graduation Year</Label>
                  <Input name="graduationYear" type="number" value={form.graduationYear} onChange={handle} placeholder="2020" min="1970" max="2030" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — Skills */}
          {step === 5 && (
            <div className="space-y-5">
              <div>
                <Label required>Relevant Skills</Label>
                <Textarea name="relevantSkills" value={form.relevantSkills} onChange={handle}
                  placeholder="e.g. Procurement negotiation, vendor management, inventory control, route optimization…"
                  error={errors.relevantSkills} />
                <FieldError msg={errors.relevantSkills} />
              </div>
              <div>
                <Label>Software Experience</Label>
                <p className="text-xs mb-3" style={{ color: '#64748b' }}>List any relevant tools (e.g. Excel, SAP, CRM systems, supply chain software)</p>
                <Textarea name="softwareExperience" value={form.softwareExperience} onChange={handle}
                  placeholder="Microsoft Excel (Advanced), SAP ERP, Salesforce CRM, Oracle SCM…" />
              </div>
            </div>
          )}

          {/* STEP 6 — Documents */}
          {step === 6 && (
            <div className="space-y-6">
              <FileUpload
                label="Upload Resume"
                required
                accept=".pdf,.doc,.docx"
                value={form.resumeFile}
                uploading={uploading.resume}
                onChange={e => handleFile('resumeFile', e)}
              />
              {errors.resumeFile && <FieldError msg={errors.resumeFile} />}
              <FileUpload
                label="Upload Cover Letter (Optional)"
                accept=".pdf,.doc,.docx"
                value={form.coverLetterFile}
                uploading={uploading.cover}
                onChange={e => handleFile('coverLetterFile', e)}
              />
            </div>
          )}

          {/* STEP 7 — References */}
          {step === 7 && (
            <div className="space-y-5">
              <div className="p-4 rounded-2xl text-sm" style={{ background: 'rgba(226,194,125,0.06)', border: '1px solid rgba(226,194,125,0.15)', color: '#94a3b8' }}>
                💡 Please provide at least one professional reference who can speak to your qualifications.
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Reference Name</Label>
                  <Input name="refName" value={form.refName} onChange={handle} placeholder="Dr. Sarah Johnson" error={errors.refName} />
                  <FieldError msg={errors.refName} />
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Input name="refRelationship" value={form.refRelationship} onChange={handle} placeholder="Former Manager" />
                </div>
                <div className="md:col-span-2">
                  <Label required>Phone / Email</Label>
                  <Input name="refContact" value={form.refContact} onChange={handle} placeholder="+1 555 000 0000 or sarah@company.com" error={errors.refContact} />
                  <FieldError msg={errors.refContact} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 8 — Declaration */}
          {step === 8 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="p-5 rounded-2xl space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-sm font-bold" style={{ color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '11px' }}>Application Summary</p>
                {[
                  ['Name', form.fullName], ['Email', form.email], ['Phone', form.phone],
                  ['Position', form.position === 'Other' ? form.positionOther : form.position],
                  ['Start Date', form.startDate], ['Employment', form.employmentType],
                  ['Employer', form.employer], ['School', form.school],
                  ['Resume', form.resumeFile?.name],
                ].map(([k, v]) => v && (
                  <div key={k} className="flex justify-between items-start gap-4 text-sm py-1 border-b" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                    <span style={{ color: '#64748b', minWidth: '100px' }}>{k}</span>
                    <span style={{ color: '#e2e8f0', textAlign: 'right' }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Declaration text */}
              <div className="p-5 rounded-2xl text-sm leading-relaxed" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: '#94a3b8' }}>
                <p className="font-semibold mb-2" style={{ color: '#cbd5e1' }}>Declaration</p>
                I, the undersigned, declare that all information provided in this application is true, complete, and accurate to the best of my knowledge. I understand that any misrepresentation or omission of material information may result in disqualification or termination of employment.
              </div>

              {/* Checkbox */}
              <button type="button" onClick={() => set('declarationConfirmed', !form.declarationConfirmed)}
                className="flex items-start gap-4 w-full text-left transition-all p-4 rounded-2xl"
                style={{
                  background: form.declarationConfirmed ? 'rgba(226,194,125,0.08)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${errors.declarationConfirmed ? '#f43f5e' : form.declarationConfirmed ? '#e2c27d44' : 'rgba(255,255,255,0.08)'}`,
                }}>
                <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-all"
                  style={{ background: form.declarationConfirmed ? '#e2c27d' : 'transparent', border: form.declarationConfirmed ? 'none' : '2px solid rgba(255,255,255,0.2)' }}>
                  {form.declarationConfirmed && <span className="text-xs font-black" style={{ color: '#0d1117' }}>✓</span>}
                </div>
                <span className="text-sm" style={{ color: form.declarationConfirmed ? '#e2c27d' : '#94a3b8' }}>
                  I confirm the information provided is accurate and complete.
                </span>
              </button>
              <FieldError msg={errors.declarationConfirmed} />

              {/* Signature */}
              <div>
                <Label required>Applicant Signature (type your full name)</Label>
                <Input name="signature" value={form.signature} onChange={handle}
                  placeholder="Type your full name as signature" error={errors.signature}
                  style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', letterSpacing: '0.02em' }} />
                <FieldError msg={errors.signature} />
              </div>

              {submitError && (
                <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5' }}>
                  ⚠ {submitError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Navigation ── */}
        <div className="flex items-center justify-between mt-6">
          <button onClick={back} disabled={step === 1}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/5"
            style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8' }}>
            ← Previous
          </button>

          <div className="flex items-center gap-2">
            {STEPS.map((s) => (
              <div key={s.id} className="transition-all rounded-full"
                style={{
                  width: step === s.id ? '24px' : '8px',
                  height: '8px',
                  background: step > s.id ? '#e2c27d88' : step === s.id ? '#e2c27d' : 'rgba(255,255,255,0.12)',
                }} />
            ))}
          </div>

          {step < 8 ? (
            <button onClick={next}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all hover:scale-105"
              style={{ background: '#e2c27d', color: '#0d1117' }}>
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: '#e2c27d', color: '#0d1117' }}>
              {submitting ? (
                <><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />Submitting…</>
              ) : '✅ Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}